<script setup>
import { computed, ref } from 'vue';
import Auth from 'dashboard/api/auth';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import Avatar from 'next/avatar/Avatar.vue';
import SidebarProfileMenuStatus from './SidebarProfileMenuStatus.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSeparator,
  DropdownItem,
} from 'next/dropdown-menu/base';
import CustomBrandPolicyWrapper from '../../components/CustomBrandPolicyWrapper.vue';

const emit = defineEmits(['close', 'openKeyShortcutModal']);

defineOptions({
  inheritAttrs: false,
});

const { t } = useI18n();

const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const accountId = useMapGetter('getCurrentAccountId');
const globalConfig = useMapGetter('globalConfig/get');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const showChatSupport = computed(() => {
  return (
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.CONTACT_CHATWOOT_SUPPORT_TEAM
    ) && globalConfig.value.chatwootInboxToken
  );
});

// Support widget remote URL: loads SDK from master installation when configured
const supportWidgetBaseUrl = computed(
  () => globalConfig.value.supportWidgetBaseUrl
);
const remoteSdkLoaded = ref(false);

const isValidSupportUrl = url => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
};

// Inject CSS to permanently hide the SDK bubble button
const injectBubbleHideCSS = () => {
  if (document.getElementById('support-widget-bubble-hide')) return;
  const style = document.createElement('style');
  style.id = 'support-widget-bubble-hide';
  style.textContent = `
    #cw-bubble-holder {
      display: none !important;
    }
  `;
  document.head.appendChild(style);
};

const loadRemoteSdk = () => {
  return new Promise((resolve, reject) => {
    // SDK already loaded (either here or by SidebarSupportBell)
    if (remoteSdkLoaded.value || window.supportSdkLoaded) {
      remoteSdkLoaded.value = true;
      resolve();
      return;
    }
    const rawUrl = supportWidgetBaseUrl.value;
    if (!isValidSupportUrl(rawUrl)) {
      reject(new Error('Invalid SUPPORT_WIDGET_BASE_URL'));
      return;
    }
    const baseUrl = rawUrl.replace(/\/+$/, '');
    // Hide bubble and position panel on the left (matches original InApp Support)
    window.chatwootSettings = {
      hideMessageBubble: true,
      position: 'left',
    };
    // Inject CSS to force-hide the bubble permanently
    injectBubbleHideCSS();
    const script = document.createElement('script');
    script.src = `${baseUrl}/packs/js/sdk.js`;
    script.async = true;
    script.onload = () => {
      if (window.chatwootSDK) {
        window.chatwootSDK.run({
          websiteToken: globalConfig.value.chatwootInboxToken,
          baseUrl,
        });
        remoteSdkLoaded.value = true;
        window.supportSdkLoaded = true;
        let resolved = false;
        const done = () => {
          if (!resolved) {
            resolved = true;
            // Identify the logged-in user in the support widget
            if (window.$chatwoot && currentUser.value) {
              const user = currentUser.value;
              window.$chatwoot.setUser(user.email || `user_${user.id}`, {
                name: user.name || user.available_name,
                email: user.email,
                avatar_url: user.avatar_url,
              });
            }
            resolve();
          }
        };
        window.addEventListener('chatwoot:ready', done, { once: true });
        setTimeout(done, 3000);
      } else {
        reject(new Error('SDK not available after script load'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load support SDK'));
    document.head.appendChild(script);
  });
};

// Declared as let to allow circular references between listener functions
// eslint-disable-next-line prefer-const
let onClickOutside;

// Track when focus goes to iframe (window blur) so we can close on return
let iframeFocused = false;

const removeAllWidgetListeners = () => {
  document.removeEventListener('click', onClickOutside, { capture: true });
  window.removeEventListener('blur', onWindowBlur);
  window.removeEventListener('focus', onWindowFocus);
};

const onWindowBlur = () => {
  // When window loses focus, it's likely because the iframe got it
  iframeFocused = true;
};

const onWindowFocus = () => {
  // When focus returns from iframe to main page, close the widget
  if (iframeFocused && window.$chatwoot && window.$chatwoot.isOpen) {
    window.$chatwoot.toggle('close');
    removeAllWidgetListeners();
  }
  iframeFocused = false;
};

const addClickOutsideListener = () => {
  document.addEventListener('click', onClickOutside, { capture: true });
  window.addEventListener('blur', onWindowBlur);
  window.addEventListener('focus', onWindowFocus);
};

onClickOutside = event => {
  const widgetHolder = document.getElementById('cw-widget-holder');
  if (
    widgetHolder &&
    !widgetHolder.contains(event.target) &&
    window.$chatwoot &&
    window.$chatwoot.isOpen
  ) {
    window.$chatwoot.toggle('close');
    removeAllWidgetListeners();
  }
};

const onWidgetClose = () => {
  removeAllWidgetListeners();
};

const handleSupportClick = async () => {
  if (supportWidgetBaseUrl.value) {
    try {
      await loadRemoteSdk();
      if (window.$chatwoot) {
        // Toggle: if already open, close it
        if (window.$chatwoot.isOpen) {
          window.$chatwoot.toggle('close');
        } else {
          window.$chatwoot.toggle('open');
          // Listen for close event from widget's internal X button
          window.addEventListener('chatwoot:close', onWidgetClose, {
            once: true,
          });
          // Add click-outside-to-close after a short delay
          // so the current click doesn't immediately close it
          setTimeout(addClickOutsideListener, 300);
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[SupportWidget] Failed to load remote SDK:', e);
    }
  } else if (window.$chatwoot) {
    window.$chatwoot.toggle();
  }
};

const isForceDarkMode = computed(() => {
  const ps = globalConfig.value?.PLATFORM_STYLE;
  return ps?.force_dark_mode === 'true' || ps?.force_dark_mode === true;
});

const menuItems = computed(() => {
  return [
    {
      show: showChatSupport.value,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.CONTACT_SUPPORT'),
      icon: 'i-lucide-life-buoy',
      click: handleSupportClick,
    },
    // NOTE: Commented out — "Atalhos do teclado" rarely used, keeping menu lighter.
    // {
    //   show: true,
    //   showOnCustomBrandedInstance: true,
    //   label: t('SIDEBAR_ITEMS.KEYBOARD_SHORTCUTS'),
    //   icon: 'i-lucide-keyboard',
    //   click: () => {
    //     emit('openKeyShortcutModal');
    //   },
    // },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.PROFILE_SETTINGS'),
      icon: 'i-lucide-user-pen',
      link: { name: 'profile_settings_index' },
    },
    {
      show: !isForceDarkMode.value,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.APPEARANCE'),
      icon: 'i-lucide-palette',
      click: () => {
        const ninja = document.querySelector('ninja-keys');
        ninja.open({ parent: 'appearance_settings' });
      },
    },
    {
      show: true,
      showOnCustomBrandedInstance: false,
      label: t('SIDEBAR_ITEMS.DOCS'),
      icon: 'i-lucide-book',
      link: 'https://www.chatwoot.com/hc/user-guide/en',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.CHANGELOG'),
      icon: 'i-lucide-sparkles',
      link: `/app/accounts/${accountId.value}/changelog`,
      nativeLink: false,
    },
    {
      show: currentUser.value.type === 'SuperAdmin',
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.SUPER_ADMIN_CONSOLE'),
      icon: 'i-lucide-castle',
      link: '/super_admin',
      nativeLink: true,
      target: '_blank',
    },
    {
      show: true,
      showOnCustomBrandedInstance: true,
      label: t('SIDEBAR_ITEMS.LOGOUT'),
      icon: 'i-lucide-power',
      click: Auth.logout,
    },
  ];
});

const allowedMenuItems = computed(() => {
  return menuItems.value.filter(item => item.show);
});
</script>

<template>
  <DropdownContainer class="relative w-full min-w-0" @close="emit('close')">
    <template #trigger="{ toggle, isOpen }">
      <button
        class="flex gap-2 items-center p-1 w-full text-left rounded-lg cursor-pointer hover:bg-n-alpha-1"
        :class="{ 'bg-n-alpha-1': isOpen }"
        @click="toggle"
      >
        <Avatar
          :size="32"
          :name="currentUser.available_name"
          :src="currentUser.avatar_url"
          :status="currentUserAvailability"
          class="flex-shrink-0"
          rounded-full
        />
        <div class="min-w-0">
          <div class="text-sm font-medium leading-4 truncate text-n-slate-12">
            {{ currentUser.available_name }}
          </div>
          <div class="text-xs truncate text-n-slate-11">
            {{ currentUser.email }}
          </div>
        </div>
      </button>
    </template>
    <DropdownBody class="bottom-12 z-50 mb-2 w-80 ltr:left-0 rtl:right-0">
      <SidebarProfileMenuStatus />
      <DropdownSeparator />
      <template v-for="item in allowedMenuItems" :key="item.label">
        <CustomBrandPolicyWrapper
          :show-on-custom-branded-instance="item.showOnCustomBrandedInstance"
        >
          <DropdownItem v-if="item.show" v-bind="item" />
        </CustomBrandPolicyWrapper>
      </template>
    </DropdownBody>
  </DropdownContainer>
</template>
